/**
 * Question Generator Center
 * AI-powered question generation with live progress monitoring
 * Ported from old frontend with exact same UI/UX
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { contentService, TopicWithDocument } from '../services/content.service';
// import { examsService } from '../services/exams.service'; // Commented out - exam dropdown is hidden
import { coursesService } from '../services/courses.service';
import { useToast } from '../contexts/ToastContext';
import { PageHeader } from '../components/PageHeader';
import { GeneratedQuestionsPreview } from '../components/GeneratedQuestionsPreview';
import { confirmDelete } from '../utils/deleteConfirmation';
import Swal from 'sweetalert2';
import {
  RefreshCw,
  Sparkles,
  Zap,
  Brain,
  FileText,
  BookOpen,
  Search,
  Upload,
  // GraduationCap, // Commented out - unused after removing ANBIMA sections
  Trash2,
  // Target, // Commented out - unused after removing ANBIMA sections
  // Award, // Commented out - unused after removing ANBIMA sections
} from 'lucide-react';

// ANBIMA Cognitive Domains (2026) - Commented out as per user request
/* const COGNITIVE_DOMAINS = [
  { value: 1, label: 'Level 1', description: 'Knowledge/Recall' },
  { value: 2, label: 'Level 2', description: 'Comprehension' },
  { value: 3, label: 'Level 3', description: 'Application' },
  { value: 4, label: 'Level 4', description: 'Analysis/Synthesis' },
]; */

// ANBIMA Behavioral Skills (2026) - Commented out as per user request
/* const BEHAVIORAL_SKILLS = [
  { value: 'context_analysis', label: 'Context Analysis' },
  { value: 'client_service', label: 'Client Service' },
  { value: 'communication', label: 'Communication' },
  { value: 'persuasion', label: 'Persuasion' },
  { value: 'professional_ethics', label: 'Professional Ethics' },
  { value: 'relationship_management', label: 'Relationship Management' },
]; */

// Command Verbs for Type 2 (Short Answer) - Commented out as per user request
/* const COMMAND_VERBS = [
  { value: 'auto', label: 'Automatic (LLM decides)' },
  { value: 'cite', label: 'Cite' },
  { value: 'explique', label: 'Explain' },
  { value: 'compare', label: 'Compare' },
  { value: 'analise', label: 'Analyze' },
]; */

interface ManualCharacterLimits {
  stemMinChars: number;
  stemMaxChars: number;
  optionMinChars: number;
  optionMaxChars: number;
  explanationMinChars: number;
  explanationMaxChars: number;
}

interface QuestionGenerationRequest {
  exam: string;
  customPromptStatus?:boolean;
  course?: string;  // Optional course ID
  courseName?: string;  // Optional course name
  count: number;  // ✅ FIXED: Changed from totalQuestions to count (matches backend field name)
  typeDistribution: {
    'mcq': number;
    'case-study': number;
    'interactive': number;
    'short-answer': number;
    'true-false': number;
    'calculation': number;
    'multiple-select': number;
  };
  difficultyDistribution: {
    easy: number;
    medium: number;
    hard: number;
  };
  topics: string[];
  oeCode?: string;  // Single OE code from selected topics
  oeCodes?: string[];  // ✅ NEW: Multiple OE codes for comprehensive coverage (Option 1)
  documentIds?: string[];  // Document IDs from selected topics (for RAG filtering)
  mcqFormat?: 'anbima' | 'short';  // MCQ format (must match backend field name)
  prompt?: string;
  manualCharacterLimits?: ManualCharacterLimits;  // Optional: Manual character limits override
}

// LocalStorage keys for draft persistence - MUST be outside component
const DRAFT_KEYS = {
  quickForm: 'questionGenerator_quickForm_draft',
  customForm: 'questionGenerator_customForm_draft',
  customPrompt: 'questionGenerator_customPrompt_draft',
  selectedTopics: 'questionGenerator_selectedTopics_draft',
  mcqFormat: 'questionGenerator_mcqFormat_draft',
  generatedQuestions: 'questionGenerator_generatedQuestions_draft',
  showPreview: 'questionGenerator_showPreview_draft',
};

// Load draft from localStorage or use defaults - MUST be outside component
const loadDraft = (key: string, defaultValue: any) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error('[QuestionGenerator] Failed to load draft:', error);
    return defaultValue;
  }
};

export default function QuestionGeneratorCenter() {
  const { accessToken } = useAuthStore();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [quickLoading, setQuickLoading] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState<string>('');

  // Topic selection state
  const [topics, setTopics] = useState<TopicWithDocument[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<TopicWithDocument[]>(() => loadDraft(DRAFT_KEYS.selectedTopics, []));
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [pollingForTopics, setPollingForTopics] = useState(false);
  const [importanceFilter, setImportanceFilter] = useState<('high' | 'medium' | 'low')[]>(['high', 'medium', 'low']);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupBy, setGroupBy] = useState<'document' | 'importance'>('document');
  const [showCustomPrompt , setShowCustomPrompt] = useState(false); // setShowCustomPrompt removed - toggle button commented out
  const [draftSavedIndicator, setDraftSavedIndicator] = useState(false);

  // Generated Questions Preview - load from draft
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>(() => loadDraft(DRAFT_KEYS.generatedQuestions, []));
  const [showPreview, setShowPreview] = useState(() => loadDraft(DRAFT_KEYS.showPreview, false));

  // Quick Generate Form - load from draft with safe defaults for new ANBIMA fields
  const [quickForm, setQuickForm] = useState(() => {
    const defaultForm = {
      exam: 'GENERIC',
      course: '',
      totalQuestions: 20,
      mcq: 70,
      caseStudy: 10,
      interactive: 10,
      shortAnswer: 10,
      easy: 30,
      medium: 50,
      hard: 20,
      // ANBIMA 2026 fields
      cognitiveDomain: undefined as number | undefined,
      behavioralSkills: [] as string[],
      questionsPerCase: 2,
      promptsPerInteractive: 5,
      commandVerb: 'auto' as string,
      validationRules: {
        prohibitedPhrases: true,
        characterLimits: true,
        structuralValidation: true,
        caseAssociation: true,
      }
    };

    const loaded = loadDraft(DRAFT_KEYS.quickForm, defaultForm);

    // Ensure new ANBIMA fields exist (for backward compatibility with old drafts)
    return {
      ...loaded,
      behavioralSkills: loaded.behavioralSkills || [],
      cognitiveDomain: loaded.cognitiveDomain,
      questionsPerCase: loaded.questionsPerCase || 2,
      promptsPerInteractive: loaded.promptsPerInteractive || 5,
      commandVerb: loaded.commandVerb || 'auto',
      validationRules: loaded.validationRules || {
        prohibitedPhrases: true,
        characterLimits: true,
        structuralValidation: true,
        caseAssociation: true,
      }
    };
  });

  // Custom Prompt Form - load from draft with safe defaults for new ANBIMA fields
  const [customPrompt, setCustomPrompt] = useState(() => loadDraft(DRAFT_KEYS.customPrompt, ''));
  const [customForm, setCustomForm] = useState(() => {
    const defaultForm = {
      exam: 'GENERIC',
      course: '',
      totalQuestions: 10,
      mcq: 70,
      caseStudy: 10,
      interactive: 10,
      shortAnswer: 10,
      easy: 30,
      medium: 50,
      hard: 20,
      // ANBIMA 2026 fields
      cognitiveDomain: undefined as number | undefined,
      behavioralSkills: [] as string[],
      questionsPerCase: 2,
      promptsPerInteractive: 5,
      commandVerb: 'auto' as string,
      validationRules: {
        prohibitedPhrases: true,
        characterLimits: true,
        structuralValidation: true,
        caseAssociation: true,
      }
    };

    const loaded = loadDraft(DRAFT_KEYS.customForm, defaultForm);

    // Ensure new ANBIMA fields exist (for backward compatibility with old drafts)
    return {
      ...loaded,
      behavioralSkills: loaded.behavioralSkills || [],
      cognitiveDomain: loaded.cognitiveDomain,
      questionsPerCase: loaded.questionsPerCase || 2,
      promptsPerInteractive: loaded.promptsPerInteractive || 5,
      commandVerb: loaded.commandVerb || 'auto',
      validationRules: loaded.validationRules || {
        prohibitedPhrases: true,
        characterLimits: true,
        structuralValidation: true,
        caseAssociation: true,
      }
    };
  });

  // MCQ Format - ANBIMA / Non-ANBIMA selection
  const [mcqFormat, setMcqFormat] = useState<'anbima' | 'short'>(() => loadDraft(DRAFT_KEYS.mcqFormat, 'anbima'));

  // Manual Character Limits - Dynamic configuration based on format
  const [manualLimits, setManualLimits] = useState<ManualCharacterLimits>({
    // ANBIMA defaults
    stemMinChars: 750,
    stemMaxChars: 800,
    optionMinChars: 100,
    optionMaxChars: 125,
    explanationMinChars: 400,
    explanationMaxChars: 600,
  });

  // Fetch exams - Commented out as exam dropdown is hidden
  /* const { data: exams = [] } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams({ status: 'active' });
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  }); */

  // Fetch all courses
  const { data: allCourses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  // Filter courses based on selected exam for quick form - Commented out as course dropdown is hidden
  /* const quickFilteredCourses = allCourses.filter(course =>
    course.linked_exams.includes(quickForm.exam)
  ); */

  // Filter courses based on selected exam for custom form - Commented out as course dropdown is hidden
  /* const customFilteredCourses = allCourses.filter(course =>
    course.linked_exams.includes(customForm.exam)
  ); */

  // Delete All Topics Mutation
  const deleteAllTopicsMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      return await contentService.deleteAllTopics(accessToken);
    },
    onSuccess: (data) => {
      setTopics([]);
      setSelectedTopics([]);
      showToast('success', 'All Topics Deleted', `Successfully deleted ${data.deleted} topic records`);
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (error: any) => {
      showToast('error', 'Delete Failed', error.response?.data?.error || 'Failed to delete topics');
    }
  });

  // Delete Single Topic Mutation
  const deleteTopicMutation = useMutation({
    mutationFn: async ({ documentId, topicTitle }: { documentId: string; topicTitle: string }) => {
      if (!accessToken) throw new Error('Not authenticated');
      return await contentService.deleteTopic(documentId, topicTitle, accessToken);
    },
    onSuccess: (_data, variables) => {
      // Remove from local state
      setTopics(prev => prev.filter(t => !(t.documentId === variables.documentId && t.title === variables.topicTitle)));
      setSelectedTopics(prev => prev.filter(t => !(t.documentId === variables.documentId && t.title === variables.topicTitle)));
      showToast('success', 'Topic Deleted', `Successfully deleted topic "${variables.topicTitle}"`);
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (error: any, variables) => {
      showToast('error', 'Delete Failed', error.response?.data?.error || `Failed to delete topic "${variables.topicTitle}"`);
    }
  });

  // Load topics on mount
  useEffect(() => {
    loadTopics(false); // Load topics silently without showing progress bar

    // Check if preview was loaded from draft (silent - no toast)
    if (showPreview && generatedQuestions.length > 0) {
      console.log('[QuestionGenerator] Restored preview from draft');
    }
  }, []);

  // Polling effect for new topics
  useEffect(() => {
    if (pollingForTopics && !loadingTopics) {
      loadTopics(false);

      const pollingInterval = setInterval(async () => {
        await loadTopics(false);
      }, 2000);

      const stopPollingTimeout = setTimeout(() => {
        setPollingForTopics(false);
        clearInterval(pollingInterval);
      }, 120000);

      return () => {
        clearInterval(pollingInterval);
        clearTimeout(stopPollingTimeout);
      };
    }
  }, [pollingForTopics, loadingTopics]);

  const loadTopics = async (showPopup: boolean = true) => {
    if (!accessToken) return;

    const previousTopicCount = topics.length;

    // Always show loading state, but different UI based on showPopup
    setLoadingTopics(true);

    if (showPopup) {
      setLoadingProgress(0);
    }

    const startTime = Date.now();
    const MIN_DISPLAY_TIME = showPopup ? 20000 : 0; // No artificial delay for silent loads

    let progressInterval: any = null;

    if (showPopup) {
      progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / MIN_DISPLAY_TIME) * 100, 100);
        setLoadingProgress(progress);
      }, 500);
    }

    try {
      const allTopics = await contentService.getAllTopics(accessToken);

      if (showPopup) {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsedTime);

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        if (progressInterval) clearInterval(progressInterval);
        setLoadingProgress(100);
      }

      setTopics(allTopics);

      const hasNewTopics = allTopics.length > previousTopicCount;

      if (allTopics.length === 0) {
        if (showPopup) {
          showToast('info', 'No Topics', 'No topics found yet. Please upload documents to generate topics automatically.');
          setPollingForTopics(true);
        }
      } else {
        if (hasNewTopics && !showPopup) {
          showToast('success', 'New Topics', `🎉 Found ${allTopics.length} topics from uploaded documents.`);
          setPollingForTopics(false);
        } else if (showPopup) {
          showToast('success', 'Topics Loaded', `✅ Loaded ${allTopics.length} topics from uploaded documents.`);
          setPollingForTopics(true);
        }
      }
    } catch (err: any) {
      console.error('Failed to load topics:', err);
      if (showPopup) {
        showToast('error', 'Load Failed', err.response?.data?.error || 'Failed to load topics');

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsedTime);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        if (progressInterval) clearInterval(progressInterval);
      }
    } finally {
      setLoadingTopics(false);
      if (showPopup) {
        setLoadingProgress(0);
      }
    }
  };

  // Auto-save Quick Form to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.quickForm, JSON.stringify(quickForm));
      console.log('[QuestionGenerator] Quick form draft saved');
      // Show saved indicator briefly
      setDraftSavedIndicator(true);
      const timer = setTimeout(() => setDraftSavedIndicator(false), 2000);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save quick form draft:', error);
    }
  }, [quickForm]);

  // Auto-save Custom Form to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.customForm, JSON.stringify(customForm));
      console.log('[QuestionGenerator] Custom form draft saved');
      setDraftSavedIndicator(true);
      const timer = setTimeout(() => setDraftSavedIndicator(false), 2000);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save custom form draft:', error);
    }
  }, [customForm]);

  // Auto-save Custom Prompt to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.customPrompt, JSON.stringify(customPrompt));
      console.log('[QuestionGenerator] Custom prompt draft saved');
      setDraftSavedIndicator(true);
      const timer = setTimeout(() => setDraftSavedIndicator(false), 2000);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save custom prompt draft:', error);
    }
  }, [customPrompt]);

  // Auto-save Selected Topics to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.selectedTopics, JSON.stringify(selectedTopics));
      console.log('[QuestionGenerator] Selected topics draft saved');
      setDraftSavedIndicator(true);
      const timer = setTimeout(() => setDraftSavedIndicator(false), 2000);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save selected topics draft:', error);
    }
  }, [selectedTopics]);

  // Auto-save MCQ Format to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.mcqFormat, JSON.stringify(mcqFormat));
      console.log('[QuestionGenerator] MCQ format draft saved:', mcqFormat);
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save MCQ format draft:', error);
    }
  }, [mcqFormat]);

  // Auto-update manual limits when format changes
  useEffect(() => {
    if (mcqFormat === 'anbima') {
      setManualLimits({
        stemMinChars: 700,
        stemMaxChars: 800,
        optionMinChars: 100,
        optionMaxChars: 125,
        explanationMinChars: 400,
        explanationMaxChars: 600,
      });
    } else {
      // Short format
      setManualLimits({
        stemMinChars: 30,
        stemMaxChars: 500,
        optionMinChars: 20,
        optionMaxChars: 150,
        explanationMinChars: 100,
        explanationMaxChars: 300,
      });
    }
  }, [mcqFormat]);

  // Reset case study & interactive to 0 when switching to non-ANBIMA format
  useEffect(() => {
    if (mcqFormat === 'short') {
      setQuickForm((prev: any) => ({
        ...prev,
        mcq: prev.mcq + prev.caseStudy + prev.interactive,
        caseStudy: 0,
        interactive: 0,
      }));
      setCustomForm((prev: any) => ({
        ...prev,
        mcq: prev.mcq + prev.caseStudy + prev.interactive,
        caseStudy: 0,
        interactive: 0,
      }));
    }
  }, [mcqFormat]);

  // Auto-select GENERIC exam when topics are selected (for file-specific generation)
  useEffect(() => {
    if (selectedTopics.length > 0) {
      // When topics from uploaded files are selected, use GENERIC exam
      // This ensures questions are generated ONLY from selected files, not exam content
      if (quickForm.exam !== 'GENERIC') {
        setQuickForm((prev: typeof quickForm) => ({ ...prev, exam: 'GENERIC', course: '' }));
      }
      if (customForm.exam !== 'GENERIC') {
        setCustomForm((prev: typeof customForm) => ({ ...prev, exam: 'GENERIC', course: '' }));
      }
    }
  }, [selectedTopics, quickForm.exam, customForm.exam]);

  // Auto-save Generated Questions to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.generatedQuestions, JSON.stringify(generatedQuestions));
      if (generatedQuestions.length > 0) {
        console.log('[QuestionGenerator] Generated questions draft saved');
      }
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save generated questions draft:', error);
    }
  }, [generatedQuestions]);

  // Auto-save Preview State to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEYS.showPreview, JSON.stringify(showPreview));
      if (showPreview) {
        console.log('[QuestionGenerator] Preview state saved');
      }
    } catch (error) {
      console.error('[QuestionGenerator] Failed to save preview state:', error);
    }
  }, [showPreview]);

  // Clear all drafts and reset forms
  const handleClearDraft = async () => {
    const result = await Swal.fire({
      title: 'Clear Draft?',
      html: `
        <p class="text-gray-600 mb-4">This will clear all saved form data and reset everything.</p>
        <p class="text-gray-700">You will need to fill out the forms again from scratch.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1e73be',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Clear Draft',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-2xl font-bold text-gray-900',
        confirmButton: 'rounded-lg px-6 py-3 font-semibold',
        cancelButton: 'rounded-lg px-6 py-3 font-semibold',
      },
      buttonsStyling: true,
    });

    if (result.isConfirmed) {
      // Clear all drafts from localStorage
      Object.values(DRAFT_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });

      // Reset all form states
      setQuickForm({
        exam: '',
        course: '',
        totalQuestions: 20,
        mcq: 70,
        caseStudy: 10,
        interactive: 10,
        shortAnswer: 10,
        easy: 30,
        medium: 50,
        hard: 20,
        cognitiveDomain: undefined,
        behavioralSkills: [],
        questionsPerCase: 2,
        promptsPerInteractive: 5,
        commandVerb: 'auto',
        validationRules: {
          prohibitedPhrases: true,
          characterLimits: true,
          structuralValidation: true,
          caseAssociation: true,
        }
      });

      setCustomForm({
        exam: '',
        course: '',
        totalQuestions: 10,
        mcq: 70,
        caseStudy: 10,
        interactive: 10,
        shortAnswer: 10,
        easy: 30,
        medium: 50,
        hard: 20,
        cognitiveDomain: undefined,
        behavioralSkills: [],
        questionsPerCase: 2,
        promptsPerInteractive: 5,
        commandVerb: 'auto',
        validationRules: {
          prohibitedPhrases: true,
          characterLimits: true,
          structuralValidation: true,
          caseAssociation: true,
        }
      });

      setCustomPrompt('');
      setSelectedTopics([]);

      // Also clear preview state
      setShowPreview(false);
      setGeneratedQuestions([]);

      showToast('success', 'Draft Cleared', 'All saved form data has been cleared');
    }
  };

  const handleDeleteAllTopics = async () => {
    const totalTopics = topics.length;

    if (totalTopics === 0) {
      showToast('info', 'No Topics', 'There are no topics to delete');
      return;
    }

    const confirmed = await confirmDelete({
      title: 'Delete ALL Topics?',
      itemName: `${totalTopics} topics`,
    });

    if (confirmed) {
      deleteAllTopicsMutation.mutate();
    }
  };

  const handleDeleteTopic = async (topic: TopicWithDocument, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent topic selection toggle

    const confirmed = await confirmDelete({
      title: 'Delete Topic?',
      itemName: topic.title,
    });

    if (confirmed) {
      deleteTopicMutation.mutate({
        documentId: topic.documentId,
        topicTitle: topic.title
      });
    }
  };

  const handleToggleTopic = (topic: TopicWithDocument) => {
    setSelectedTopics(prev => {
      const isSelected = prev.some(t => t.title === topic.title && t.documentId === topic.documentId);
      if (isSelected) {
        return prev.filter(t => !(t.title === topic.title && t.documentId === topic.documentId));
      } else {
        return [...prev, topic];  // Store full topic object with relatedOECodes
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredTopics();
    const allSelected = filtered.every(topic =>
      selectedTopics.some(t => t.title === topic.title && t.documentId === topic.documentId)
    );
    if (allSelected) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics(filtered);  // Store full topic objects
    }
  };

  const getFilteredTopics = () => {
    let filtered = topics.filter(topic => importanceFilter.includes(topic.importance));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(topic =>
        topic.title.toLowerCase().includes(query) ||
        topic.description.toLowerCase().includes(query) ||
        topic.keywords.some(k => k.toLowerCase().includes(query)) ||
        topic.documentTitle.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const getGroupedTopics = () => {
    const filtered = getFilteredTopics();

    if (groupBy === 'document') {
      const grouped = new Map<string, TopicWithDocument[]>();
      filtered.forEach(topic => {
        const docTitle = topic.documentTitle;
        if (!grouped.has(docTitle)) {
          grouped.set(docTitle, []);
        }
        grouped.get(docTitle)!.push(topic);
      });
      return grouped;
    } else {
      const grouped = new Map<string, TopicWithDocument[]>();
      ['high', 'medium', 'low'].forEach(imp => {
        const topicsForImp = filtered.filter(t => t.importance === imp);
        if (topicsForImp.length > 0) {
          grouped.set(imp, topicsForImp);
        }
      });
      return grouped;
    }
  };

  const handleQuestionTypeChange = (type: 'mcq' | 'caseStudy' | 'interactive' | 'shortAnswer', newValue: number) => {
    const others = ['mcq', 'caseStudy', 'interactive', 'shortAnswer'].filter(t => t !== type) as ('mcq' | 'caseStudy' | 'interactive' | 'shortAnswer')[];

    if (newValue === 100) {
      setQuickForm({
        ...quickForm,
        [type]: 100,
        [others[0]]: 0,
        [others[1]]: 0,
        [others[2]]: 0,
      });
    } else {
      const remaining = 100 - newValue;
      const currentOthersTotal = others.reduce((sum, t) => sum + quickForm[t], 0);

      if (currentOthersTotal === 0) {
        const equalShare = Math.floor(remaining / others.length);
        const remainder = remaining % others.length;
        setQuickForm({
          ...quickForm,
          [type]: newValue,
          [others[0]]: equalShare + remainder,
          [others[1]]: equalShare,
          [others[2]]: equalShare,
        });
      } else {
        const ratio = remaining / currentOthersTotal;
        const newValues: any = { [type]: newValue };
        let distributed = 0;

        others.forEach((t, idx) => {
          if (idx === others.length - 1) {
            newValues[t] = remaining - distributed;
          } else {
            const value = Math.round(quickForm[t] * ratio);
            newValues[t] = value;
            distributed += value;
          }
        });

        setQuickForm({ ...quickForm, ...newValues });
      }
    }
  };

  const handleDifficultyChange = (level: 'easy' | 'medium' | 'hard', newValue: number) => {
    const others = ['easy', 'medium', 'hard'].filter(l => l !== level) as ('easy' | 'medium' | 'hard')[];

    if (newValue === 100) {
      setQuickForm({
        ...quickForm,
        [level]: 100,
        [others[0]]: 0,
        [others[1]]: 0,
      });
    } else {
      const remaining = 100 - newValue;
      const currentOthersTotal = others.reduce((sum, l) => sum + quickForm[l], 0);

      if (currentOthersTotal === 0) {
        const equalShare = Math.floor(remaining / others.length);
        const remainder = remaining % others.length;
        setQuickForm({
          ...quickForm,
          [level]: newValue,
          [others[0]]: equalShare + remainder,
          [others[1]]: equalShare,
        });
      } else {
        const ratio = remaining / currentOthersTotal;
        const newValues: any = { [level]: newValue };
        let distributed = 0;

        others.forEach((l, idx) => {
          if (idx === others.length - 1) {
            newValues[l] = remaining - distributed;
          } else {
            const value = Math.round(quickForm[l] * ratio);
            newValues[l] = value;
            distributed += value;
          }
        });

        setQuickForm({ ...quickForm, ...newValues });
      }
    }
  };

  // Custom form handlers
  const handleCustomQuestionTypeChange = (type: 'mcq' | 'caseStudy' | 'interactive' | 'shortAnswer', newValue: number) => {
    const others = ['mcq', 'caseStudy', 'interactive', 'shortAnswer'].filter(t => t !== type) as ('mcq' | 'caseStudy' | 'interactive' | 'shortAnswer')[];

    if (newValue === 100) {
      setCustomForm({
        ...customForm,
        [type]: 100,
        [others[0]]: 0,
        [others[1]]: 0,
        [others[2]]: 0,
      });
    } else {
      const remaining = 100 - newValue;
      const currentOthersTotal = others.reduce((sum, t) => sum + customForm[t], 0);

      if (currentOthersTotal === 0) {
        const equalShare = Math.floor(remaining / others.length);
        const remainder = remaining % others.length;
        setCustomForm({
          ...customForm,
          [type]: newValue,
          [others[0]]: equalShare + remainder,
          [others[1]]: equalShare,
          [others[2]]: equalShare,
        });
      } else {
        const ratio = remaining / currentOthersTotal;
        const newValues: any = { [type]: newValue };
        let distributed = 0;

        others.forEach((t, idx) => {
          if (idx === others.length - 1) {
            newValues[t] = remaining - distributed;
          } else {
            const value = Math.round(customForm[t] * ratio);
            newValues[t] = value;
            distributed += value;
          }
        });

        setCustomForm({ ...customForm, ...newValues });
      }
    }
  };

  const handleCustomDifficultyChange = (level: 'easy' | 'medium' | 'hard', newValue: number) => {
    const others = ['easy', 'medium', 'hard'].filter(l => l !== level) as ('easy' | 'medium' | 'hard')[];

    if (newValue === 100) {
      setCustomForm({
        ...customForm,
        [level]: 100,
        [others[0]]: 0,
        [others[1]]: 0,
      });
    } else {
      const remaining = 100 - newValue;
      const currentOthersTotal = others.reduce((sum, l) => sum + customForm[l], 0);

      if (currentOthersTotal === 0) {
        const equalShare = Math.floor(remaining / others.length);
        const remainder = remaining % others.length;
        setCustomForm({
          ...customForm,
          [level]: newValue,
          [others[0]]: equalShare + remainder,
          [others[1]]: equalShare,
        });
      } else {
        const ratio = remaining / currentOthersTotal;
        const newValues: any = { [level]: newValue };
        let distributed = 0;

        others.forEach((l, idx) => {
          if (idx === others.length - 1) {
            newValues[l] = remaining - distributed;
          } else {
            const value = Math.round(customForm[l] * ratio);
            newValues[l] = value;
            distributed += value;
          }
        });

        setCustomForm({ ...customForm, ...newValues });
      }
    }
  };

  // Helper: Handle behavioral skill toggle for quick form - Commented out as behavioral skills section is hidden
  /* const handleQuickBehavioralSkillToggle = (skill: string) => {
    if (quickForm.behavioralSkills.includes(skill)) {
      setQuickForm({
        ...quickForm,
        behavioralSkills: quickForm.behavioralSkills.filter((s: string) => s !== skill)
      });
    } else {
      setQuickForm({
        ...quickForm,
        behavioralSkills: [...quickForm.behavioralSkills, skill]
      });
    }
  }; */

  // Helper: Handle behavioral skill toggle for custom form - Commented out as behavioral skills section is hidden
  /* const handleCustomBehavioralSkillToggle = (skill: string) => {
    if (customForm.behavioralSkills.includes(skill)) {
      setCustomForm({
        ...customForm,
        behavioralSkills: customForm.behavioralSkills.filter((s: string) => s !== skill)
      });
    } else {
      setCustomForm({
        ...customForm,
        behavioralSkills: [...customForm.behavioralSkills, skill]
      });
    }
  }; */

  const handleQuickGenerate = async () => {
    if (!accessToken) return;

    if (selectedTopics.length === 0) {
      showToast('error', 'No Topics', 'Please select at least one topic');
      return;
    }

    const totalTypePercent = quickForm.mcq + quickForm.caseStudy + quickForm.interactive + quickForm.shortAnswer;
    if (totalTypePercent !== 100) {
      showToast('error', 'Invalid Distribution', `Question Type Distribution must total 100%. Current: ${totalTypePercent}%`);
      return;
    }

    const totalDiffPercent = quickForm.easy + quickForm.medium + quickForm.hard;
    if (totalDiffPercent !== 100) {
      showToast('error', 'Invalid Distribution', `Difficulty Distribution must total 100%. Current: ${totalDiffPercent}%`);
      return;
    }

    setQuickLoading(true);

    try {
      // Calculate type distribution with proper rounding to ensure sum equals totalQuestions
      const calculateDistribution = (total: number, percentages: number[]) => {
        const calculated = percentages.map(p => total * p / 100);
        const floored = calculated.map(Math.floor);
        let remaining = total - floored.reduce((a, b) => a + b, 0);

        // Distribute remaining questions to types with largest fractional parts
        const fractionalParts = calculated.map((val, idx) => ({ idx, frac: val - floored[idx] }));
        fractionalParts.sort((a, b) => b.frac - a.frac);

        for (let i = 0; i < remaining; i++) {
          floored[fractionalParts[i].idx]++;
        }

        return floored;
      };

      const typePercentages = [quickForm.mcq, quickForm.caseStudy, quickForm.interactive, quickForm.shortAnswer];
      const [mcqCount, caseStudyCount, interactiveCount, shortAnswerCount] = calculateDistribution(
        quickForm.totalQuestions,
        typePercentages
      );

      // Extract all OE codes from selected topics
      const allOECodes = selectedTopics.reduce((codes, topic) => {
        if (topic.relatedOECodes && topic.relatedOECodes.length > 0) {
          codes.push(...topic.relatedOECodes);
        }
        return codes;
      }, [] as string[]);

      // Remove duplicates
      const uniqueOECodes = Array.from(new Set(allOECodes));

      // Extract unique document IDs from selected topics
      const allDocumentIds = selectedTopics
        .map(topic => topic.documentId)
        .filter(Boolean);
      const uniqueDocumentIds = Array.from(new Set(allDocumentIds));

      console.log('[QuestionGenerator] Filtering by document IDs from selected topics:', uniqueDocumentIds);
      console.log('[QuestionGenerator] 🎯 Found OE codes from selected topics:', uniqueOECodes);

      // Find selected course to get course name
      const selectedCourse = allCourses.find(c => c.course_id === quickForm.course);

      // ✅ OPTION 2: LLM-DRIVEN OE PRIORITIZATION: Send all OE codes + question count, LLM selects different OEs
      let result: any;

      if (uniqueOECodes.length > 1) {
        console.log(`[QuestionGenerator] 📊 INTELLIGENT OE MODE (OPTION 2): Generating ${quickForm.totalQuestions} questions with LLM selecting different OE codes`);
        console.log(`[QuestionGenerator] 🧠 OE codes available to LLM:`, uniqueOECodes);

        // ✅ OPTION 2: Make 1 SINGLE REQUEST with count=N
        // LLM will generate N questions with DIFFERENT OE codes
        const request: QuestionGenerationRequest = {
          exam: quickForm.exam,
          course: quickForm.course || undefined,
          courseName: selectedCourse?.name || undefined,
          count: quickForm.totalQuestions,  // ✅ Ask for exact number of questions (e.g., 5)
          typeDistribution: {
            'mcq': mcqCount,
            'case-study': caseStudyCount,
            'interactive': interactiveCount,
            'short-answer': shortAnswerCount,
            'true-false': 0,
            'calculation': 0,
            'multiple-select': 0,
          },
          difficultyDistribution: {
            easy: quickForm.easy,
            medium: quickForm.medium,
            hard: quickForm.hard,
          },
          topics: selectedTopics.map(t => t.title),
          oeCodes: uniqueOECodes, // ✅ Send ALL OE codes - LLM will SELECT different ones for each question!
          documentIds: uniqueDocumentIds.length > 0 ? uniqueDocumentIds : undefined,
          mcqFormat: mcqFormat,
          manualCharacterLimits: manualLimits,
        };

        console.log(`[QuestionGenerator] 📤 Sending 1 request: count=${quickForm.totalQuestions}, oeCodes=${uniqueOECodes.length}`);

        try {
          result = await contentService.startGeneration(request, accessToken);
          if (result && (result as any).questions) {
            console.log(`[QuestionGenerator] ✅ LLM Generated ${(result as any).questions.length} questions with DIFFERENT OE codes`);
            // Log which OE code each question got
            (result as any).questions.forEach((q: any, idx: number) => {
              console.log(`[QuestionGenerator]   Q${idx + 1}: oeCode = ${q.oeCode}`);
            });
          }
        } catch (error) {
          console.error(`[QuestionGenerator] ❌ Failed to generate questions:`, error);
          throw error;
        }

        console.log(`[QuestionGenerator] 🎉 INTELLIGENT GENERATION COMPLETE: Generated ${(result as any).questions?.length || 0} questions with LLM-selected different OE codes ✅`);

      } else {
        // SINGLE OE MODE: Standard generation
        console.log('[QuestionGenerator] 📄 SINGLE-OE MODE: Standard generation');

        const request: QuestionGenerationRequest = {
          exam: quickForm.exam,
          course: quickForm.course || undefined,
          courseName: selectedCourse?.name || undefined,
          count: quickForm.totalQuestions,  // ✅ CHANGED FROM totalQuestions to count
          typeDistribution: {
            'mcq': mcqCount,
            'case-study': caseStudyCount,
            'interactive': interactiveCount,
            'short-answer': shortAnswerCount,
            'true-false': 0,
            'calculation': 0,
            'multiple-select': 0,
          },
          difficultyDistribution: {
            easy: quickForm.easy,
            medium: quickForm.medium,
            hard: quickForm.hard,
          },
          topics: selectedTopics.map(t => t.title),
          oeCode: uniqueOECodes.length > 0 ? uniqueOECodes[0] : undefined,
          documentIds: uniqueDocumentIds.length > 0 ? uniqueDocumentIds : undefined,
          mcqFormat: mcqFormat,
          manualCharacterLimits: manualLimits,
        };

        console.log('[QuestionGenerator] Sending generation request:', request);
        console.log('[QuestionGenerator] 🎯 Manual Character Limits being sent:', manualLimits);
        result = await contentService.startGeneration(request, accessToken);
      }
      console.log('[QuestionGenerator] Generation result:', result);

      // Check if questions were actually generated
      if (result && (result as any).questions && (result as any).questions.length > 0) {
        const questions = (result as any).questions;

        // Log source files for each question
        console.log('[QuestionGenerator] 📄 Source Files per Question:');
        questions.forEach((q: any, idx: number) => {
          if (q.sourceDocuments && q.sourceDocuments.length > 0) {
            console.log(`  Question ${idx + 1}:`, q.sourceDocuments.map((d: any) => d.filename).join(', '));
          } else {
            console.log(`  Question ${idx + 1}: No source files`);
          }
        });

        // Quick Generate: Save directly to database without preview
        console.log(`[QuestionGenerator] Quick Generate: Saving ${questions.length} questions directly to database...`);

        try {
          const { questionsService } = await import('../services/questions.service');
          const saveResult = await questionsService.batchSaveQuestions(questions);

          if (saveResult.success) {
            // Clear draft after successful save
            Object.values(DRAFT_KEYS).forEach(key => {
              localStorage.removeItem(key);
            });

            // Reset forms
            setQuickForm({
              exam: '',
              course: '',
              totalQuestions: 20,
              mcq: 70,
              caseStudy: 10,
              interactive: 10,
              shortAnswer: 10,
              easy: 30,
              medium: 50,
              hard: 20,
              cognitiveDomain: undefined,
              behavioralSkills: [],
              questionsPerCase: 2,
              promptsPerInteractive: 5,
              commandVerb: 'auto',
              validationRules: {
                prohibitedPhrases: true,
                characterLimits: true,
                structuralValidation: true,
                caseAssociation: true,
              }
            });
            setSelectedTopics([]);

            // Invalidate cache
            queryClient.invalidateQueries({ queryKey: ['questions'] });

            showToast('success', 'Questions Saved', `${saveResult.data.totalSaved} questions saved directly to Question Bank!`);
            navigate('/questions');
          } else {
            showToast('error', 'Save Failed', 'Failed to save questions to database');
          }
        } catch (saveError: any) {
          console.error('[QuestionGenerator] Quick Generate save error:', saveError);
          showToast('error', 'Save Failed', saveError.response?.data?.error || saveError.message || 'Failed to save questions');
        }
      } else {
        showToast('error', 'Generation Failed', 'No questions were generated');
      }

      setQuickLoading(false);
    } catch (err: any) {
      console.error('[QuestionGenerator] Generation error:', err);
      showToast('error', 'Generation Failed', err.response?.data?.error || err.message || 'Failed to generate questions');
      setQuickLoading(false);
    }
  };

  const handleCustomGenerate = async () => {
    if (!accessToken || !customPrompt.trim()) return;

    // 🎨 NEW: Custom Prompt Mode - No topics required!
    // AI will search semantically across all documents

    const customTypeTotal = customForm.mcq + customForm.caseStudy + customForm.interactive + customForm.shortAnswer;
    if (customTypeTotal !== 100) {
      showToast('error', 'Invalid Distribution', `Question Type Distribution must total 100%. Current: ${customTypeTotal}%`);
      return;
    }

    const customDiffTotal = customForm.easy + customForm.medium + customForm.hard;
    if (customDiffTotal !== 100) {
      showToast('error', 'Invalid Distribution', `Difficulty Distribution must total 100%. Current: ${customDiffTotal}%`);
      return;
    }

    setCustomLoading(true);
    setCustomError(''); // Clear any previous errors

    try {
      console.log('[QuestionGenerator] 🎨 Starting CUSTOM PROMPT generation (NEW API)');
      console.log('[QuestionGenerator] Prompt:', customPrompt.substring(0, 100));
      console.log('[QuestionGenerator] Exam:', customForm.exam);
      console.log('[QuestionGenerator] Total Questions:', customForm.totalQuestions);
      // Calculate difficulty percentages
      const difficultyPercent = {
        easy: customForm.easy,
        medium: customForm.medium,
        hard: customForm.hard
      };

      // Determine primary difficulty (highest percentage)
      const primaryDifficulty = Object.entries(difficultyPercent)
        .sort(([, a], [, b]) => b - a)[0][0];

      // Determine primary question type (highest percentage)
      const typePercent = {
        mcq: customForm.mcq,
        caseStudy: customForm.caseStudy,
        interactive: customForm.interactive,
        shortAnswer: customForm.shortAnswer
      };
      const primaryType = Object.entries(typePercent)
        .sort(([, a], [, b]) => b - a)[0][0];

      // Map to backend question type
      const questionTypeMap: Record<string, string> = {
        mcq: 'MCQ',
        caseStudy: 'case_study',
        interactive: 'interactive',
        shortAnswer: 'short_answer'
      };

      // Extract document IDs from selected topics (optional)
      const documentIds = selectedTopics.length > 0
        ? Array.from(new Set(selectedTopics.map(t => t.documentId).filter(Boolean)))
        : undefined;

      // Find selected course
      const selectedCourse = allCourses.find(c => c.course_id === customForm.course);

      console.log('[QuestionGenerator] 📤 Calling NEW Custom Prompt API');
      console.log('[QuestionGenerator] Question Type:', questionTypeMap[primaryType]);
      console.log('[QuestionGenerator] Difficulty:', primaryDifficulty);
      console.log('[QuestionGenerator] Document IDs:', documentIds);

      // 🎨 Call NEW Custom Prompt API
      const result = await contentService.generateWithCustomPrompt(
        customPrompt,
        customForm.exam,
        customForm.totalQuestions,
        {
          questionType: questionTypeMap[primaryType],
          difficulty: primaryDifficulty,
          documentIds: documentIds,
          course: customForm.course || undefined,
          courseName: selectedCourse?.name || undefined
        },
        accessToken
      );

      console.log('[QuestionGenerator] ✅ Custom Prompt API response:', result);

      // Check if questions were generated
      if (result && result.questions && result.questions.length > 0) {
        const questions = result.questions;

        // Log metadata
        console.log('[QuestionGenerator] 📊 Generation Metadata:', result.metadata);
        if (result.metadata?.format === 'custom') {
          console.log('[QuestionGenerator] ✅ Custom Prompt Template Used!');
        }

        // Log source files for each question
        console.log('[QuestionGenerator] 📄 Source Files per Question:');
        questions.forEach((q: any, idx: number) => {
          if (q.sourceDocuments && q.sourceDocuments.length > 0) {
            console.log(`  Question ${idx + 1}:`, q.sourceDocuments.map((d: any) => d.filename).join(', '));
          } else {
            console.log(`  Question ${idx + 1}: No source files`);
          }
        });

        // Add temp IDs for tracking
        const questionsWithIds = questions.map((q: any, idx: number) => ({
          ...q,
          tempId: `temp-${Date.now()}-${idx}`
        }));

        // Show preview
        setGeneratedQuestions(questionsWithIds);
        setShowPreview(true);

        // Show success message with custom format badge
        const message = result.metadata?.format === 'custom'
          ? `✨ ${questions.length} Custom Prompt questions ready for review!`
          : `${questions.length} questions ready for review!`;
        showToast('success', 'Questions Generated', message);
      } else {
        showToast('error', 'Generation Failed', 'No questions were generated');
      }

      setCustomLoading(false);
    } catch (err: any) {
      console.error('[QuestionGenerator] Custom generation error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate questions';

      // Set error state to display below button
      setCustomError(errorMessage);

      setCustomLoading(false);
    }
  };

  const handleSaveQuestions = async (questions: any[]) => {
    try {
      // Import questions service
      const { questionsService } = await import('../services/questions.service');

      // Save questions to database using batch save endpoint
      console.log(`[QuestionGenerator] Saving ${questions.length} questions to database...`);
      const result = await questionsService.batchSaveQuestions(questions);

      if (result.success) {
        // Invalidate cache so Question Bank refreshes
        queryClient.invalidateQueries({ queryKey: ['questions'] });

        // Clear all drafts from localStorage after successful save
        Object.values(DRAFT_KEYS).forEach(key => {
          localStorage.removeItem(key);
        });
        console.log('[QuestionGenerator] Draft cleared after successful save');

        // Reset all form states to default
        setQuickForm({
          exam: '',
          course: '',
          totalQuestions: 20,
          mcq: 70,
          caseStudy: 10,
          interactive: 10,
          shortAnswer: 10,
          easy: 30,
          medium: 50,
          hard: 20,
          cognitiveDomain: undefined,
          behavioralSkills: [],
          questionsPerCase: 2,
          promptsPerInteractive: 5,
          commandVerb: 'auto',
          validationRules: {
            prohibitedPhrases: true,
            characterLimits: true,
            structuralValidation: true,
            caseAssociation: true,
          }
        });

        setCustomForm({
          exam: '',
          course: '',
          totalQuestions: 10,
          mcq: 70,
          caseStudy: 10,
          interactive: 10,
          shortAnswer: 10,
          easy: 30,
          medium: 50,
          hard: 20,
          cognitiveDomain: undefined,
          behavioralSkills: [],
          questionsPerCase: 2,
          promptsPerInteractive: 5,
          commandVerb: 'auto',
          validationRules: {
            prohibitedPhrases: true,
            characterLimits: true,
            structuralValidation: true,
            caseAssociation: true,
          }
        });

        setCustomPrompt('');
        setSelectedTopics([]);

        showToast('success', 'Questions Saved', `${result.data.totalSaved} questions saved to database! Draft cleared.`);
        setShowPreview(false);
        setGeneratedQuestions([]);
        navigate('/questions');
      } else {
        throw new Error('Batch save failed');
      }
    } catch (error: any) {
      console.error('[QuestionGenerator] Save error:', error);
      showToast('error', 'Save Failed', error.response?.data?.error || error.message || 'Failed to save questions');
    }
  };

  const handleCancelPreview = () => {
    // Clear preview state and generated questions
    setShowPreview(false);
    setGeneratedQuestions([]);

    // Clear from localStorage
    localStorage.removeItem(DRAFT_KEYS.generatedQuestions);
    localStorage.removeItem(DRAFT_KEYS.showPreview);
    console.log('[QuestionGenerator] Preview draft cleared');
  };

  const totalTypePercent = quickForm.mcq + quickForm.caseStudy + quickForm.interactive + quickForm.shortAnswer;
  const totalDiffPercent = quickForm.easy + quickForm.medium + quickForm.hard;

  const customTotalTypePercent = customForm.mcq + customForm.caseStudy + customForm.interactive + customForm.shortAnswer;
  const customTotalDiffPercent = customForm.easy + customForm.medium + customForm.hard;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="AI Generator Center"
          description="Intelligent question generation powered by advanced AI technology"
          breadcrumbs={[{ label: 'Generator Center' }]}
        />
        <div className="flex items-center gap-3">
          {/* Draft Saved Indicator */}
          {draftSavedIndicator && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium animate-fade-in">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Draft Saved
            </div>
          )}
          {/* Clear Draft Button */}
          <button
            onClick={handleClearDraft}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
            title="Clear all saved form data"
          >
            <Trash2 className="w-4 h-4" />
            Clear Draft
          </button>
        </div>
      </div>

      {/* Enhanced Main Content Card */}
      <div className="rounded-2xl shadow-lg bg-white border border-gray-200 p-8">
          {/* Enhanced Topic Selection Section - Hidden when custom prompt is enabled */}
          {!showCustomPrompt && (
          <div className="rounded-2xl border-2 border-gray-200 bg-white p-6 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#4A90C8] flex items-center justify-center shadow-md">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Content Topics from Uploaded Documents</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#6B46C1]">{topics.length} topics</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm font-semibold text-[#ea7500]">{new Set(topics.map(t => t.documentId)).size} documents</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                  ✓ {selectedTopics.length} selected
                </span>
                <button
                  onClick={() => loadTopics()}
                  disabled={loadingTopics}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-50"
                  title="Refresh topics"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingTopics ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleDeleteAllTopics}
                  disabled={deleteAllTopicsMutation.isPending || topics.length === 0}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete all topics"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {loadingTopics && loadingProgress > 0 ? (
              <div className="p-8 text-center rounded-xl border-2 border-[#4A90C8] bg-blue-50">
                <h3 className="text-xl font-bold text-[#ea7500] mb-2">AI is Analyzing Your Documents</h3>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Please Wait - Extracting Topics</h4>
                <p className="text-gray-600 mb-6 text-sm max-w-lg mx-auto">
                  Our AI is carefully extracting topics from your uploaded documents. This process may take a few moments.
                </p>

                <div className="mb-6 px-8">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-[#ea7500]">Processing...</span>
                    <span className="text-sm font-medium text-[#ea7500]">{Math.round(loadingProgress)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-[#4A90C8] to-[#ea7500]"
                      style={{
                        width: `${loadingProgress}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {loadingProgress < 100 ? `Time remaining: ~${Math.max(0, Math.ceil((100 - loadingProgress) * 0.2))} seconds` : 'Almost done...'}
                  </p>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <span className="px-6 py-2.5 rounded-lg bg-orange-100 text-[#ea7500] font-medium text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Extracting Topics
                  </span>
                  <span className="px-6 py-2.5 rounded-lg bg-orange-100 text-[#ea7500] font-medium text-sm flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Analyzing Content
                  </span>
                </div>
              </div>
            ) : loadingTopics ? (
              <div className="p-8 text-center rounded-xl border-2 border-gray-200 bg-gray-50">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <RefreshCw className="w-6 h-6 text-[#4A90C8] animate-spin" />
                  <span className="text-lg font-semibold text-gray-700">Loading topics...</span>
                </div>
                <p className="text-sm text-gray-500">Fetching topics from your documents</p>
              </div>
            ) : topics.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-[#4A90C8] flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No Topics Available Yet</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
                  Topics are automatically extracted from PDFs when you upload them. If you've recently uploaded documents, topics may still be processing.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => loadTopics()}
                    className="px-5 py-2.5 rounded-lg border-2 border-[#4A90C8] text-[#4A90C8] font-semibold transition-all hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Refresh Topics
                  </button>
                  <button
                    onClick={() => (window.location.href = '/materials')}
                    className="px-5 py-2.5 rounded-lg bg-[#4A90C8] text-white font-semibold transition-all shadow-md hover:shadow-lg hover:bg-[#3A7FB8] flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Documents
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Search and Filters */}
                <div className="mb-4">
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search topics, keywords, or documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-transparent outline-none transition-all"
                    />
                  </div>

                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                      <p className="text-xs text-gray-600 mb-2">Filter by Importance</p>
                      <div className="flex gap-2">
                        {(['high', 'medium', 'low'] as const).map(imp => (
                          <button
                            key={imp}
                            onClick={() => {
                              if (importanceFilter.includes(imp)) {
                                if (importanceFilter.length > 1) {
                                  setImportanceFilter(importanceFilter.filter(i => i !== imp));
                                }
                              } else {
                                setImportanceFilter([...importanceFilter, imp]);
                              }
                            }}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                              importanceFilter.includes(imp) ? 'shadow-sm' : 'opacity-50'
                            }`}
                            style={{
                              color: imp === 'high' ? '#ef4444' : imp === 'medium' ? '#f59e0b' : '#10b981',
                              background: importanceFilter.includes(imp)
                                ? `rgba(${imp === 'high' ? '239,68,68' : imp === 'medium' ? '245,158,11' : '16,185,129'}, 0.15)`
                                : 'transparent',
                            }}
                          >
                            {imp.charAt(0).toUpperCase() + imp.slice(1)} ({topics.filter(t => t.importance === imp).length})
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 mb-2">Group By</p>
                      <div className="flex gap-2">
                        {(['document', 'importance'] as const).map(group => (
                          <button
                            key={group}
                            onClick={() => setGroupBy(group)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                              groupBy === group ? 'bg-orange-100 text-[#ea7500]' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {group.charAt(0).toUpperCase() + group.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm font-semibold text-gray-700">{getFilteredTopics().length} topic(s) found</p>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm font-semibold text-fk-orange-vibrant hover:bg-orange-50 px-3 py-1 rounded-lg transition-colors"
                  >
                    {selectedTopics.length === getFilteredTopics().length ? 'Deselect All' : 'Select All Filtered'}
                  </button>
                </div>

                <div
                  className="max-h-96 overflow-auto rounded-xl border border-gray-200"
                  style={{ background: 'rgba(0,0,0,0.01)' }}
                >
                  {Array.from(getGroupedTopics()).map(([groupName, groupTopics]) => (
                    <div key={groupName} className="mb-4 last:mb-0">
                      {/* Group Header */}
                      <div
                        className="sticky top-0 px-4 py-3 border-b border-gray-100 z-10 backdrop-blur-sm"
                        style={{ background: 'rgba(30, 115, 190, 0.08)' }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            {groupBy === 'document' ? (
                              <>
                                <FileText className="w-5 h-5 text-fk-orange-vibrant" />
                                <span className="font-bold text-gray-900">{groupName}</span>
                              </>
                            ) : (
                              <>
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    background: groupName === 'high' ? '#ef4444' : groupName === 'medium' ? '#f59e0b' : '#10b981',
                                  }}
                                />
                                <span className="font-bold text-gray-900 capitalize">{groupName} Priority</span>
                              </>
                            )}
                            <span className="px-2 py-0.5 rounded-md bg-white text-xs font-semibold text-gray-600">
                              {groupTopics.length} topics
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              const allSelected = groupTopics.every(topic =>
                                selectedTopics.some(s => s.title === topic.title && s.documentId === topic.documentId)
                              );
                              if (allSelected) {
                                setSelectedTopics(prev => prev.filter(s =>
                                  !groupTopics.some(topic => topic.title === s.title && topic.documentId === s.documentId)
                                ));
                              } else {
                                setSelectedTopics(prev => {
                                  const newSelection = [...prev];
                                  groupTopics.forEach(topic => {
                                    if (!newSelection.some(s => s.title === topic.title && s.documentId === topic.documentId)) {
                                      newSelection.push(topic);  // Store full topic object
                                    }
                                  });
                                  return newSelection;
                                });
                              }
                            }}
                            className="text-xs font-semibold text-fk-orange-vibrant hover:bg-white px-2 py-1 rounded-md transition-colors"
                          >
                            {groupTopics.every(t => selectedTopics.some(s => s.title === t.title && s.documentId === t.documentId)) ? 'Deselect' : 'Select'} All
                          </button>
                        </div>
                      </div>

                      {/* Group Topics */}
                      {groupTopics.map((topic) => {
                        const importanceColor =
                          topic.importance === 'high' ? '#ef4444' :
                          topic.importance === 'medium' ? '#f59e0b' : '#10b981';
                        const isSelected = selectedTopics.some(s => s.title === topic.title && s.documentId === topic.documentId);

                        return (
                          <div
                            key={`${topic.documentId}-${topic.title}`}
                            onClick={() => handleToggleTopic(topic)}
                            className={`px-4 py-4 border-b border-gray-100 cursor-pointer hover:bg-orange-50 transition-colors ${
                              isSelected ? 'bg-green-50' : ''
                            }`}
                          >
                            <div className="flex gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-bold text-gray-900 flex-1">{topic.title}</h4>
                                  <span
                                    className="px-2 py-0.5 rounded-md text-xs font-bold uppercase"
                                    style={{
                                      background: `rgba(${importanceColor === '#ef4444' ? '239,68,68' : importanceColor === '#f59e0b' ? '245,158,11' : '16,185,129'}, 0.12)`,
                                      color: importanceColor,
                                    }}
                                  >
                                    {topic.importance}
                                  </span>
                                  <button
                                    onClick={(e) => handleDeleteTopic(topic, e)}
                                    disabled={deleteTopicMutation.isPending}
                                    className="ml-2 p-1.5 rounded-lg hover:bg-red-100 transition-colors text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Delete this topic"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{topic.description}</p>

                                {/* Keywords */}
                                {topic.keywords.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-semibold text-gray-500 mb-1">Keywords:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {topic.keywords.slice(0, 5).map((keyword) => (
                                        <span
                                          key={`${topic.title}-${keyword}`}
                                          className="px-2 py-0.5 rounded-md text-xs font-medium"
                                          style={{
                                            background: 'rgba(30, 115, 190, 0.08)',
                                            color: '#1e73be',
                                          }}
                                        >
                                          {keyword}
                                        </span>
                                      ))}
                                      {topic.keywords.length > 5 && (
                                        <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600">
                                          +{topic.keywords.length - 5}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <div className="flex flex-wrap gap-1">
                                  {groupBy !== 'document' && (
                                    <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-700 flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      {topic.documentTitle}
                                    </span>
                                  )}
                                  {topic.pageReferences.length > 0 && (
                                    <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600">
                                      Pages: {topic.pageReferences.slice(0, 3).join(', ')}{topic.pageReferences.length > 3 ? '...' : ''}
                                    </span>
                                  )}
                                  {(topic.relatedOECodes && topic.relatedOECodes.length > 0 ? (
                                    <>
                                      {topic.relatedOECodes.slice(0, 5).map((oeCode, idx) => (
                                        <span
                                          key={`${topic.title}-${oeCode}-${idx}`}
                                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300"
                                          title={`OE Code: ${oeCode}`}
                                        >
                                          {oeCode}
                                        </span>
                                      ))}
                                      {topic.relatedOECodes.length > 5 && (
                                        <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600 font-medium">
                                          +{topic.relatedOECodes.length - 5} more
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-gray-100 text-gray-500 border border-gray-300"
                                      title="No OE Code"
                                    >
                                      N/A
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          )}

          {/* Enhanced Generator Options */}
          <div className="space-y-6">
            {/* Enhanced Quick Generate Card */}
            <div className="rounded-2xl border-2 border-gray-200 p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#4A90C8] flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Quick Generate</h3>
                  <p className="text-xs text-gray-600">Configure distribution and generate instantly</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Exam dropdown - Commented out */}
                {/* <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                    Exam
                  </label>
                  <select
                    value={quickForm.exam}
                    onChange={(e) => setQuickForm({ ...quickForm, exam: e.target.value, course: '' })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90C8] focus:border-[#4A90C8] outline-none transition-all bg-white hover:border-gray-400"
                    disabled={selectedTopics.length > 0}
                  >
                    <option value="GENERIC">GENERIC</option>
                    {exams.map(exam => (
                      <option key={exam.exam_id} value={exam.exam_id}>{exam.exam_id} - {exam.name}</option>
                    ))}
                  </select>
                  {selectedTopics.length > 0 && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-800">
                        <strong>📄 File-Specific Mode:</strong> Questions will be generated ONLY from your selected {selectedTopics.length} topic(s), not from exam content. Exam automatically set to "GENERIC".
                      </p>
                    </div>
                  )}
                </div> */}

                {/* Course dropdown - Commented out */}
                {/* <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-blue-600" />
                    Course (Optional)
                  </label>
                  <select
                    value={quickForm.course}
                    onChange={(e) => setQuickForm({ ...quickForm, course: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90C8] focus:border-[#4A90C8] outline-none transition-all bg-white hover:border-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    disabled={quickFilteredCourses.length === 0}
                  >
                    <option value="">All Courses</option>
                    {quickFilteredCourses.map(course => (
                      <option key={course.course_id} value={course.course_id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                  {quickFilteredCourses.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2 ml-1">No courses linked to this exam</p>
                  )}
                </div> */}

                {/* Exam and Question Format on same line */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Exam Radio Button - Default: GENERIC */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Exam
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="quickExam"
                          value="GENERIC"
                          checked={quickForm.exam === 'GENERIC'}
                          onChange={(e) => setQuickForm({ ...quickForm, exam: e.target.value })}
                          className="w-4 h-4 text-[#4A90C8] border-gray-300 focus:ring-[#4A90C8]"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">GENERIC</span>
                      </label>
                    </div>
                  </div>

                  {/* MCQ Format Selection - ANBIMA / Non-ANBIMA */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Question Format
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="mcqFormat"
                          value="anbima"
                          checked={mcqFormat === 'anbima'}
                          onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'short')}
                          className="w-4 h-4 text-[#4A90C8] border-gray-300 focus:ring-[#4A90C8]"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">ANBIMA Format</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="mcqFormat"
                          value="short"
                          checked={mcqFormat === 'short'}
                          onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'short')}
                          className="w-4 h-4 text-[#4A90C8] border-gray-300 focus:ring-[#4A90C8]"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Non-ANBIMA Format</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Total Questions
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={quickForm.totalQuestions}
                    onChange={(e) => setQuickForm({ ...quickForm, totalQuestions: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90C8] focus:border-[#4A90C8] outline-none transition-all bg-white hover:border-gray-400 font-semibold"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-gray-700">Question Types</label>
                    <span className={`text-xs font-bold ${totalTypePercent === 100 ? 'text-green-600' : 'text-red-600'}`}>
                      ({totalTypePercent}%)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: 'mcq' as const, label: 'MCQ' },
                      { key: 'caseStudy' as const, label: 'Case Study', anbimaOnly: true },
                      { key: 'interactive' as const, label: 'Interactive', anbimaOnly: true },
                      { key: 'shortAnswer' as const, label: 'Short Answer' },
                    ].map(({ key, label, anbimaOnly }) => (
                      // Hide Short Answer always; hide Case Study & Interactive for non-ANBIMA format
                      key !== 'shortAnswer' && !(anbimaOnly && mcqFormat === 'short') && (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">{label}</span>
                          <span className="font-semibold">{quickForm[key]}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={quickForm[key]}
                          onChange={(e) => handleQuestionTypeChange(key, parseInt(e.target.value))}
                          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, #1e73be 0%, #1e73be ${quickForm[key]}%, #e5e7eb ${quickForm[key]}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                      )
                    ))}
                  </div>
                </div>

                {/* MCQ Character Limits - Show when MCQ percentage > 0 */}
                {quickForm.mcq > 0 && (
                  <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50 space-y-4">
                    <h3 className="font-semibold text-gray-900 mb-2">MCQ Character Limits ({mcqFormat === 'anbima' ? 'ANBIMA' : 'Short'} Format)</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Configure character limits for MCQ generation (counts without spaces)
                    </p>

                    <div className="space-y-4">
                        {/* Question Stem Limits */}
                        <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Question Stem</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Min Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.stemMinChars}
                                onChange={(e) => setManualLimits({...manualLimits, stemMinChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="10"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.stemMaxChars}
                                onChange={(e) => setManualLimits({...manualLimits, stemMaxChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="20"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Each Option Limits */}
                        <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Each Option (A, B, C, D)</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Min Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.optionMinChars}
                                onChange={(e) => setManualLimits({...manualLimits, optionMinChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="10"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.optionMaxChars}
                                onChange={(e) => setManualLimits({...manualLimits, optionMaxChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="20"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Explanation Limits */}
                        <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Explanation</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Min Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.explanationMinChars}
                                onChange={(e) => setManualLimits({...manualLimits, explanationMinChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="50"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max Chars
                              </label>
                              <input
                                type="number"
                                value={manualLimits.explanationMaxChars}
                                onChange={(e) => setManualLimits({...manualLimits, explanationMaxChars: parseInt(e.target.value) || 0})}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                min="100"
                              />
                            </div>
                          </div>
                        </div>

                      <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
                        <strong>💡 Note:</strong> Character counts exclude spaces. Limits auto-update when you change format.
                      </div>
                    </div>
                  </div>
                )}

                {/* ANBIMA 2026 sections - Commented out as per user request */}
                {/* <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50 space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    Skills & Cognitive Domain (ANBIMA 2026)
                  </h3>
                  ... (Cognitive Domain, Behavioral Skills sections)
                </div>

                {quickForm.caseStudy > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Questions per Case (Type 3)
                    </label>
                    ... (Questions per Case section)
                  </div>
                )}

                {quickForm.interactive > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Prompts per Interactive (Type 4)
                    </label>
                    ... (Prompts per Interactive section)
                  </div>
                )}

                {quickForm.shortAnswer > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Command Verb (Type 2)
                    </label>
                    ... (Command Verb section)
                  </div>
                )}

                <div className="p-4 rounded-lg border-2 border-yellow-300 bg-yellow-50">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-yellow-600" />
                    ANBIMA Validation Rules
                  </h3>
                  ... (Validation Rules section)
                </div> */}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-gray-700">Difficulty Distribution</label>
                    <span className={`text-xs font-bold ${totalDiffPercent === 100 ? 'text-green-600' : 'text-red-600'}`}>
                      ({totalDiffPercent}%)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: 'easy' as const, label: 'Easy', color: '#10b981' },
                      { key: 'medium' as const, label: 'Medium', color: '#f59e0b' },
                      { key: 'hard' as const, label: 'Hard', color: '#ef4444' },
                    ].map(({ key, label, color }) => (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-1">
                          <span style={{ color }}>{label}</span>
                          <span className="font-semibold">{quickForm[key]}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={quickForm[key]}
                          onChange={(e) => handleDifficultyChange(key, parseInt(e.target.value))}
                          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${color} 0%, ${color} ${quickForm[key]}%, #e5e7eb ${quickForm[key]}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {selectedTopics.length === 0 ? (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800 font-medium">
                    💡 Please select at least one topic above to generate questions
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-green-800 mb-2">
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">✓</div>
                      Questions will be generated ONLY from these {selectedTopics.length} selected topic(s):
                    </div>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {selectedTopics.map((topic, idx) => (
                        <div key={`${topic.documentId}-${topic.title}`} className="text-xs text-green-700 pl-7">
                          {idx + 1}. {topic.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleQuickGenerate}
                  disabled={quickLoading || selectedTopics.length === 0 || totalTypePercent !== 100 || totalDiffPercent !== 100}
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed bg-[#4A90C8] hover:bg-[#3A7FB8]"
                >
                  <div className="flex items-center justify-center gap-2">
                    {quickLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    <span>{quickLoading ? 'Generating...' : 'Generate Questions'}</span>
                  </div>
                </button>

                {totalTypePercent !== 100 && (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 font-medium">
                    Question Types must equal 100%. Current: {totalTypePercent}%
                  </div>
                )}
                {totalDiffPercent !== 100 && (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 font-medium">
                    Difficulty must equal 100%. Current: {totalDiffPercent}%
                  </div>
                )}
              </div>
            </div>

            {/* Toggle Button for Custom Prompt - Commented out */}
             <div className="flex justify-center">
              <button
                onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                className="group relative px-8 py-4 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                style={{
                  background: showCustomPrompt
                    ? 'linear-gradient(135deg, #ea7500 0%, #ff8c00 100%)'
                    : 'linear-gradient(135deg, #4A90C8 0%, #5fa3d8 100%)'
                }}
              >
                <div className="flex items-center gap-3">
                  {showCustomPrompt ? (
                    <>
                      <Zap className="w-5 h-5" />
                      <span>Hide Custom Prompt Mode</span>
                      <span className="px-2 py-1 bg-white/20 rounded-lg text-xs">Advanced</span>
                    </>
                  ) : (
                    <>
                      <Brain className="w-5 h-5" />
                      <span>Enable Custom Prompt Mode</span>
                      <Sparkles className="w-5 h-5" />
                    </>
                  )}
                </div>
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
              </button>
            </div> 

            {/* Enhanced Custom Prompt Card */}
            {showCustomPrompt && (
              <div className="rounded-2xl border-2 border-gray-200 p-6 bg-white shadow-sm hover:shadow-md transition-shadow animate-fadeIn">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#ea7500] flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Custom Prompt</h3>
                  <p className="text-xs text-gray-600">Use natural language to describe what you need</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Exam dropdown - Commented out */}
                {/* <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-orange-600" />
                    Exam
                  </label>
                  <select
                    value={customForm.exam}
                    onChange={(e) => setCustomForm({ ...customForm, exam: e.target.value, course: '' })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-orange-100 focus:border-orange-400 outline-none transition-all duration-300 bg-white hover:border-orange-300 shadow-sm"
                    disabled={selectedTopics.length > 0}
                  >
                    <option value="GENERIC">GENERIC</option>
                    {exams.map(exam => (
                      <option key={exam.exam_id} value={exam.exam_id}>{exam.exam_id} - {exam.name}</option>
                    ))}
                  </select>
                  {selectedTopics.length > 0 && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-800">
                        <strong>📄 File-Specific Mode:</strong> Questions will be generated ONLY from your selected {selectedTopics.length} topic(s), not from exam content. Exam automatically set to "GENERIC".
                      </p>
                    </div>
                  )}
                </div> */}

                {/* Course dropdown - Commented out */}
                {/* <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-orange-600" />
                    Course (Optional)
                  </label>
                  <select
                    value={customForm.course}
                    onChange={(e) => setCustomForm({ ...customForm, course: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#ea7500] focus:border-[#ea7500] outline-none transition-all bg-white hover:border-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    disabled={customFilteredCourses.length === 0}
                  >
                    <option value="">All Courses</option>
                    {customFilteredCourses.map(course => (
                      <option key={course.course_id} value={course.course_id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                  {customFilteredCourses.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2 ml-1">No courses linked to this exam</p>
                  )}
                </div> */}

                {/* Exam Radio Button - Default: GENERIC */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Exam
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="customExam"
                        value="GENERIC"
                        checked={customForm.exam === 'GENERIC'}
                        onChange={(e) => setCustomForm({ ...customForm, exam: e.target.value })}
                        className="w-4 h-4 text-[#ea7500] border-gray-300 focus:ring-[#ea7500]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">GENERIC</span>
                    </label>
                  </div>
                </div>

                {/* Question Format */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    Question Format
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="customMcqFormat"
                        value="anbima"
                        checked={mcqFormat === 'anbima'}
                        onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'short')}
                        className="w-4 h-4 text-[#ea7500] border-gray-300 focus:ring-[#ea7500]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">ANBIMA Format</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="customMcqFormat"
                        value="short"
                        checked={mcqFormat === 'short'}
                        onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'short')}
                        className="w-4 h-4 text-[#ea7500] border-gray-300 focus:ring-[#ea7500]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Non-ANBIMA Format</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    Number of Questions
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={customForm.totalQuestions}
                    onChange={(e) => setCustomForm({ ...customForm, totalQuestions: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-orange-100 focus:border-orange-400 outline-none transition-all duration-300 bg-white hover:border-orange-300 shadow-sm font-bold text-lg"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-gray-700">Question Types</label>
                    <span className={`text-xs font-bold ${customTotalTypePercent === 100 ? 'text-green-600' : 'text-red-600'}`}>
                      ({customTotalTypePercent}%)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: 'mcq' as const, label: 'MCQ' },
                      { key: 'caseStudy' as const, label: 'Case Study', anbimaOnly: true },
                      { key: 'interactive' as const, label: 'Interactive', anbimaOnly: true },
                      { key: 'shortAnswer' as const, label: 'Short Answer' },
                    ].map(({ key, label, anbimaOnly }) => (
                      // Hide Short Answer always; hide Case Study & Interactive for non-ANBIMA format
                      key !== 'shortAnswer' && !(anbimaOnly && mcqFormat === 'short') && (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">{label}</span>
                          <span className="font-semibold">{customForm[key]}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={customForm[key]}
                          onChange={(e) => handleCustomQuestionTypeChange(key, parseInt(e.target.value))}
                          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, #ea7500 0%, #ea7500 ${customForm[key]}%, #e5e7eb ${customForm[key]}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                      )
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-gray-700">Difficulty Distribution</label>
                    <span className={`text-xs font-bold ${customTotalDiffPercent === 100 ? 'text-green-600' : 'text-red-600'}`}>
                      ({customTotalDiffPercent}%)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: 'easy' as const, label: 'Easy', color: '#10b981' },
                      { key: 'medium' as const, label: 'Medium', color: '#f59e0b' },
                      { key: 'hard' as const, label: 'Hard', color: '#ef4444' },
                    ].map(({ key, label, color }) => (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-1">
                          <span style={{ color }}>{label}</span>
                          <span className="font-semibold">{customForm[key]}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={customForm[key]}
                          onChange={(e) => handleCustomDifficultyChange(key, parseInt(e.target.value))}
                          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${color} 0%, ${color} ${customForm[key]}%, #e5e7eb ${customForm[key]}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ANBIMA 2026 sections - Commented out as per user request */}
                {/* <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50 space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    Skills & Cognitive Domain (ANBIMA 2026)
                  </h3>
                  ... (Cognitive Domain, Behavioral Skills sections)
                </div>

                {customForm.caseStudy > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Questões por Case (Tipo 3)
                    </label>
                    ... (Questions per Case section)
                  </div>
                )}

                {customForm.interactive > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Prompts por Interativa (Tipo 4)
                    </label>
                    ... (Prompts per Interactive section)
                  </div>
                )}

                {customForm.shortAnswer > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Verbo de Comando (Tipo 2)
                    </label>
                    ... (Command Verb section)
                  </div>
                )}

                <div className="p-4 rounded-lg border-2 border-yellow-300 bg-yellow-50">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-yellow-600" />
                    Regras de Validação ANBIMA
                  </h3>
                  ... (Validation Rules section)
                </div> */}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-orange-600" />
                    Custom Prompt
                  </label>
                  <textarea
                    rows={7}
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Example: Generate 10 challenging MCQ questions about Brazilian Treasury Bonds (Tesouro Direto), focusing on the differences between LTN, LFT, and NTN-B. Include questions that test understanding of duration, indexation, and mark-to-market concepts."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#ea7500] focus:border-[#ea7500] outline-none transition-all bg-white hover:border-gray-400 resize-none"
                  />
                </div>

                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800 font-medium">
                  🤖 AI will  find relevant content and generate questions based on your prompt
                </div>

                <button
                  onClick={handleCustomGenerate}
                  disabled={customLoading || !customPrompt.trim() || customTotalTypePercent !== 100 || customTotalDiffPercent !== 100}
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed bg-[#ea7500] hover:bg-[#d66a00]"
                >
                  <div className="flex items-center justify-center gap-2">
                    {customLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <Brain className="w-5 h-5" />
                    )}
                    <span>{customLoading ? 'Generating...' : 'Generate with AI Prompt'}</span>
                  </div>
                </button>

                {customError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 font-medium">
                    ❌ {customError}
                  </div>
                )}

                {customTotalTypePercent !== 100 && (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 font-medium">
                    Question Types must equal 100%. Current: {customTotalTypePercent}%
                  </div>
                )}
                {customTotalDiffPercent !== 100 && (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 font-medium">
                    Difficulty must equal 100%. Current: {customTotalDiffPercent}%
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Generated Questions Preview */}
            {showPreview && generatedQuestions.length > 0 && (
              <div className="mt-8">
                <GeneratedQuestionsPreview
                  questions={generatedQuestions}
                  onSave={handleSaveQuestions}
                  onCancel={handleCancelPreview}
                />
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
